import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLang } from "../i18n";
import { CheckMark } from "./Logo";

const maskParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const maskLine = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export default function Hero() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const yBlob = useTransform(scrollY, [0, 600], [0, 120]);
  const yBlob2 = useTransform(scrollY, [0, 600], [0, -80]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section
      id="hero"
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-20"
      data-testid="hero-section"
    >
      {/* parallax accents */}
      <motion.div
        style={{ y: yBlob }}
        className="pointer-events-none absolute -top-10 -right-24 h-[520px] w-[520px] rounded-full bg-teal/10 blur-3xl"
      />
      <motion.div
        style={{ y: yBlob2 }}
        className="pointer-events-none absolute bottom-0 -left-32 h-[420px] w-[420px] rounded-full bg-coral/10 blur-3xl"
      />

      <motion.div style={{ opacity }} className="relative mx-auto max-w-7xl px-5 md:px-10 w-full">
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-2 rounded-full border border-charcoal/15 bg-crema/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-warmgray"
          data-testid="hero-badge"
        >
          <CheckMark size={16} /> Central Florida
        </motion.div>

        <motion.h1
          variants={maskParent}
          initial="hidden"
          animate="show"
          className="font-display font-black tracking-tighter leading-[0.92] text-charcoal text-5xl sm:text-7xl lg:text-8xl max-w-4xl"
          data-testid="hero-headline"
        >
          <span className="reveal-mask">
            <motion.span variants={maskLine} className="block">
              {t.hero.line1}
            </motion.span>
          </span>
          <span className="reveal-mask mt-1">
            <motion.span variants={maskLine} className="block text-teal">
              {t.hero.line2}
            </motion.span>
          </span>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9 }}
          className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5"
        >
          <button
            onClick={() => navigate("/book")}
            className="group inline-flex items-center gap-3 rounded-full bg-coral text-crema text-base font-semibold pl-7 pr-6 py-4 hover:brightness-105 transition-all duration-300 shadow-[0_10px_40px_-12px_rgba(216,90,48,0.7)]"
            data-testid="hero-cta"
          >
            {t.hero.cta}
            <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
}
